import { useMemo, useCallback, useState } from "react";

import { Form } from "antd";
import { clsx } from "clsx";
import uniqBy from "lodash/uniqBy";

import {
    RVFormItem,
    RVSimpleTextFloatingPlaceholder,
    ConfigurableVariableOption,
    ConfigurableVariablesPickerPlugin,
    CustomVariablesHighlightPlugin,
    PlainTextEditor,
} from "@regal-voice/ui";
import { useTriggerEventNode } from "Pages/orchestration/Pages/journeys/JourneyEditPage/Components/JourneyBuilder/shared/hooks/useTriggerEventNode";
import { useEventsAttributesFullListQuery } from "ServerData/EventSchema/Hooks/useEventsAttributesQuery/useEventsAttributesQuery";
import { createOptionsWithPrefix } from "ServerData/EventSchema/Utils/eventSchemaUtils";

import styles from "./CustomAITaskLexInput.module.scss";

// @todo replace with @regal-voice/ui/components/organisms/RVLexicalEditor/utils/constants
export const HANDLEBAR_TRIGGERS = [{ openingTag: "{{", closingTag: "}}" }];
const HANDLEBARS_WRAP = /\{\{.*?\}\}/;
const HANDLEBARS_REGEX_LIST = [HANDLEBARS_WRAP];
type InputProps = {
    onChange: (value: string) => void;
    formClassName: string;
    name: string;
    rules?: Array<any>;
    label: string;
    isSingleLine: boolean;
};

export function CustomAITaskLexInput({ onChange, formClassName, name, rules, label, isSingleLine }: InputProps) {
    const { triggerNode } = useTriggerEventNode();

    const text = Form.useWatch(name);

    const { eventAttributes, loading } = useEventsAttributesFullListQuery(triggerNode);

    const eventOptions = useMemo(() => {
        const options = eventAttributes.map((event) => createOptionsWithPrefix(event, "event")) || [];
        const items = options.map(
            (option) => new ConfigurableVariableOption(option.label, option.value, option.dataType)
        );
        return uniqBy(items, "value");
    }, [eventAttributes]);

    const [isTextEditorEmpty, setIsTextEditorEmpty] = useState(!text);
    const [displayFloatingLabel, setDisplayFloatingLabel] = useState(!!text);

    const onEditorFocus = useCallback(() => {
        setDisplayFloatingLabel(isTextEditorEmpty);
    }, [isTextEditorEmpty]);

    const onEditorBlur = useCallback(() => {
        setDisplayFloatingLabel(!isTextEditorEmpty);
    }, [isTextEditorEmpty]);

    const onValueChange = useCallback(
        (value: string) => {
            // remove any potential leading or trailing whitespace
            onChange(value.trim());
            setIsTextEditorEmpty(!value);
        },
        [onChange]
    );

    return (
        <RVFormItem className={formClassName}>
            <div
                className={clsx(styles.textAreaWrapper, {
                    [styles.singleLine]: isSingleLine,
                    [styles.multiLine]: !isSingleLine,
                    [styles.floatingPlaceholder]: displayFloatingLabel,
                })}
            >
                <RVSimpleTextFloatingPlaceholder
                    placeholderText={label}
                    displayFloatingLabel={displayFloatingLabel}
                    isTextEditorEmpty={isTextEditorEmpty}
                    isSingleLine={isSingleLine}
                />
                <div className={clsx(styles.lexicalContainer)}>
                    <RVFormItem noStyle name={name} rules={rules} validateTrigger={["onSubmit"]}>
                        <PlainTextEditor
                            data-testid="custom-ai-task-lexical-editor"
                            className={styles.editorHasContainer}
                            inputClassName={styles.inputHasContainer}
                            formItemName={name}
                            onFocus={onEditorFocus}
                            onBlur={onEditorBlur}
                            onChange={onValueChange}
                        >
                            <CustomVariablesHighlightPlugin prioritiziedRegexList={HANDLEBARS_REGEX_LIST} />
                            <ConfigurableVariablesPickerPlugin
                                popAbove
                                loading={loading}
                                fixedWidth
                                prioritizedTriggers={HANDLEBAR_TRIGGERS}
                                options={eventOptions}
                            />
                        </PlainTextEditor>
                    </RVFormItem>
                </div>
            </div>
        </RVFormItem>
    );
}
